import {
  BusinessCenter as NoOfficesIcon,
  Add as AddIcon,
  Business as BusinessIcon
} from '@mui/icons-material'
import { Avatar, Box, Typography, Paper, Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'

import routes from '../../pages/routes'
import { OfficeWithRole } from '../../utils/profileHelpers'

import OfficeCard from './OfficeCard'

/**
 * Props for the OfficeList component
 */
export interface OfficeListProps {
  /** Array of offices with role information */
  offices: OfficeWithRole[]
  /** Loading state indicator */
  loading?: boolean
}

/**
 * OfficeList - Office list display component
 *
 * Displays all offices user has access to.
 * Handles empty state when user has no office access.
 * Organizes offices in a clean, readable layout.
 */
const OfficeList = ({ offices, loading = false }: OfficeListProps) => {
  const navigate = useNavigate()

  // Handle navigation to office creation page
  const handleCreateOffice = () => {
    navigate(routes.createOffice)
  }

  // Show loading state
  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            <BusinessIcon fontSize='small' />
          </Avatar>
          <Typography variant='h6' component='h2' sx={{ fontWeight: 600 }}>
            Office Memberships
          </Typography>
        </Box>
        <Typography variant='body2' color='text.secondary'>
          Loading your office memberships...
        </Typography>
      </Box>
    )
  }

  // Show empty state when user has no office access
  if (!offices || offices.length === 0) {
    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <BusinessIcon fontSize='small' />
            </Avatar>
            <Typography variant='h6' component='h2' sx={{ fontWeight: 600 }}>
              Office Memberships
            </Typography>
          </Box>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleCreateOffice}
            sx={{ ml: 2 }}
          >
            Create Office
          </Button>
        </Box>
        <Paper
          variant='outlined'
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: 'background.default'
          }}
        >
          <NoOfficesIcon
            sx={{
              fontSize: 48,
              color: 'text.secondary',
              mb: 2
            }}
          />
          <Typography variant='body1' color='text.secondary' gutterBottom>
            No office memberships found
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            You don't have access to any offices yet. Create your own office or
            contact an office administrator to get invited.
          </Typography>
          <Button
            variant='outlined'
            size='large'
            startIcon={<AddIcon />}
            onClick={handleCreateOffice}
            data-testid='create-first-office-button'
          >
            Create Your First Office
          </Button>
        </Paper>
      </Box>
    )
  }

  // Display offices in a clean, readable layout
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            <BusinessIcon fontSize='small' />
          </Avatar>
          <Typography variant='h6' component='h2' sx={{ fontWeight: 600 }}>
            Office Memberships
          </Typography>
        </Box>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={handleCreateOffice}
          sx={{ ml: 2 }}
        >
          Create Office
        </Button>
      </Box>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        You have access to {offices.length} office
        {offices.length !== 1 ? 's' : ''}
      </Typography>

      <Box>
        {offices.map(office => (
          <OfficeCard
            key={office.oid || `office-${Math.random()}`}
            office={office}
          />
        ))}
      </Box>
    </Box>
  )
}

export default OfficeList
